import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import LandingCtaButton from './LandingCtaButton';
import { LANDING_COLORS, landingEyebrowSx } from './landingTokens';

const PlanCard = ({
  name,
  tagline,
  price,
  priceSuffix,
  priceNote,
  rows,
  note,
  addOns = [],
  addOnsTitle,
  comingSoonLabel,
  ctaLabel,
  onCtaClick,
  dark = false,
}) => {
  const textColor = dark ? LANDING_COLORS.onDark : LANDING_COLORS.navy;
  const mutedColor = dark ? LANDING_COLORS.onDarkMuted : LANDING_COLORS.muted;
  const lineColor = dark ? LANDING_COLORS.onDarkLine : LANDING_COLORS.lineSoft;
  const accentColor = dark ? LANDING_COLORS.terracottaLight : LANDING_COLORS.terracotta;

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        background: dark ? LANDING_COLORS.navy : LANDING_COLORS.paper,
        border: `1px solid ${dark ? LANDING_COLORS.navy : LANDING_COLORS.line}`,
        boxShadow: 'none',
        color: textColor,
      }}
    >
      <Box
        sx={{
          height: 3,
          background: dark ? LANDING_COLORS.terracotta : LANDING_COLORS.line,
        }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: { xs: 3, md: 4 } }}>
        <Typography component="span" sx={{ ...landingEyebrowSx, color: accentColor }}>
          {name}
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '1rem', mt: 1, color: textColor }}>
          {tagline}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 3 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2.3rem', md: '2.7rem' },
              lineHeight: 1,
              letterSpacing: priceSuffix ? '-0.02em' : '0.06em',
              color: textColor,
            }}
          >
            {price}
          </Typography>
          {priceSuffix && (
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.95rem', color: mutedColor }}>
              {priceSuffix}
            </Typography>
          )}
        </Stack>
        <Typography sx={{ fontSize: '0.82rem', color: mutedColor, mt: 1.25, minHeight: { md: 40 } }}>
          {priceNote}
        </Typography>

        <Box sx={{ mt: 3, flex: 1 }}>
          <Box sx={{ borderTop: `1px solid ${lineColor}` }}>
            {rows.map((row) => (
              <Stack
                key={row.key}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{ py: 1.4, borderBottom: `1px solid ${lineColor}` }}
              >
                {row.included ? (
                  <CheckRoundedIcon sx={{ fontSize: 18, mt: '2px', color: accentColor }} />
                ) : (
                  <RemoveRoundedIcon sx={{ fontSize: 18, mt: '2px', color: mutedColor, opacity: 0.55 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: row.included ? 600 : 500,
                      fontSize: '0.9rem',
                      color: row.included ? textColor : mutedColor,
                    }}
                  >
                    {row.label}
                  </Typography>
                  {row.note && (
                    <Typography sx={{ fontSize: '0.75rem', color: mutedColor, mt: 0.25 }}>
                      {row.note}
                    </Typography>
                  )}
                </Box>
                {row.comingSoon && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: accentColor,
                      border: `1px solid ${accentColor}`,
                      borderRadius: '999px',
                      px: 1,
                      py: '2px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {comingSoonLabel}
                  </Typography>
                )}
              </Stack>
            ))}
          </Box>

          {note && (
            <Typography sx={{ mt: 2.5, fontSize: '0.8rem', color: mutedColor, lineHeight: 1.7 }}>
              {note}
            </Typography>
          )}
        </Box>

        {addOns.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography component="span" sx={{ ...landingEyebrowSx, color: mutedColor }}>
              {addOnsTitle}
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {addOns.map((addOn) => (
                <Stack key={addOn.label} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ fontSize: '0.88rem', color: mutedColor }}>{addOn.label}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: textColor }}>
                    {addOn.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <LandingCtaButton
          fullWidth
          size="large"
          tone={dark ? 'light' : 'accent'}
          onClick={onCtaClick}
          sx={{ mt: 4, py: 1.4 }}
        >
          {ctaLabel}
        </LandingCtaButton>
      </Box>
    </Paper>
  );
};

export default PlanCard;
